// src/components/MsoTrace.tsx
//
// ★★ ANNEX A 22 — a stored MSO calculation trace, rendered in the order the engine took it.
//
// ══ WHAT THIS IS FOR ══
//
// The engine explains every calculation: which rule it chose and why, which decision row
// matched, and for each bill component the exact share, the rounded share and the cent
// Government absorbed. This component is the ONE rendering of that record, shared by the
// calculator (a result just computed), the enquiry detail (a result stored earlier) and the
// editor's test panel (a case run against a draft), so the three cannot describe the same
// trace three ways.
//
// ⚠ **The steps are shown in `step` order and NEVER re-sorted or filtered.** A trace is an
// account of a sequence; a step moved or hidden changes what the account says. The list is an
// `<ol>` so a screen reader announces the position as well as the words.
//
// ⚠ **Every word is the engine's own.** `summary` is the engine's sentence with its machine tokens read as words
// (`plainSummary`: BILL_AMOUNT shows as "Bill amount"); no figure or other word changes. The only thing this
// component adds is the component table for `COMPONENT_CALCULATED`, built from that step's own
// `detail` — it re-derives no share and applies no rate (the exact figure is the server's
// own four-place string, printed as it arrived).
//
// ⚠ **A step it cannot read is still shown.** An unrecognised `code`, or a `detail` of an
// unexpected shape, falls back to the summary alone rather than vanishing: a trace that hid
// the step it did not understand would be the one place a surprise is least welcome.
import { Money } from './Money'
import { CategoryBadge } from './StatusBadge'
import { humaniseToken, MSO_COMPONENT_LABELS, basisPointsLabel, plainSummary, exactCentsAsMoney } from '../lib/mso-vocabulary'
import type { MsoTraceStep } from '../lib/types'

/** The engine's step codes, in plain words. A badge carries the TEXT, so meaning is never colour alone. */
const CODE_LABELS: Readonly<Record<string, string>> = {
  INPUT_VALIDATED: 'Input checked',
  INPUT_CONSISTENT: 'Input consistent',
  ENTITLEMENT_COVERS: 'Entitlement',
  CAP_STATUS: 'Annual cap',
  CANDIDATES: 'Candidate rules',
  RULE_SELECTED: 'Rule selected',
  REQUIRED_INPUTS: 'Required inputs',
  ROW_MATCHED: 'Row matched',
  COMPONENT_CALCULATED: 'Component',
  TOTALLED: 'Total',
  REFUSED: 'Refused',
}

const isStep = (value: unknown): value is MsoTraceStep =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as MsoTraceStep).step === 'number' &&
  typeof (value as MsoTraceStep).code === 'string' &&
  typeof (value as MsoTraceStep).summary === 'string'

/**
 * Read a stored trace of unknown provenance as a list of steps.
 * `unknown` in, because the enquiry reads it from a `jsonb` column the page did not write.
 */
export function readTrace(trace: unknown): readonly MsoTraceStep[] {
  return Array.isArray(trace) ? trace.filter(isStep) : []
}

const num = (value: unknown): number | null => (typeof value === 'number' && Number.isSafeInteger(value) ? value : null)
const text = (value: unknown): string | null => (typeof value === 'string' ? value : null)

function ComponentTable({ detail }: { detail: Readonly<Record<string, unknown>> }) {
  const component = text(detail['component'])
  const amount = num(detail['amountCents'])
  const basisPoints = num(detail['officerBasisPoints'])
  const exact = text(detail['exactOfficerCents'])
  const officer = num(detail['officerCents'])
  const government = num(detail['governmentCents'])
  const adjustment = text(detail['roundingAdjustment'])
  if (
    component === null ||
    amount === null ||
    basisPoints === null ||
    exact === null ||
    officer === null ||
    government === null ||
    adjustment === null
  ) {
    return null
  }
  const name = MSO_COMPONENT_LABELS[component] ?? humaniseToken(component)
  const shares: readonly (readonly [string, React.ReactNode])[] = [
    ['Amount', <Money key="a" cents={amount} />],
    ['Officer share', <span key="s" className="pt-tabular">{basisPointsLabel(basisPoints)}</span>],
    ['Exact', <span key="e" className="pt-tabular">{exactCentsAsMoney(exact)}</span>],
    ['Rounded', <Money key="r" cents={officer} />],
    ['Rounding adjustment', <span key="j" className="pt-tabular">{adjustment === '0.0000' ? 'None' : exactCentsAsMoney(adjustment)}</span>],
    ['Government', <Money key="g" cents={government} />],
  ]
  return (
    <div className="mt-2">
    {/*
      ⚠ TWO renderings of ONE calculation, one visible at a time by CSS (display:none removes the other from the
      accessibility tree too). The seven-column table is 825px wide, which on a phone left only Component and Amount
      visible behind a sideways scroll (UI review, 2026-10-14); under the `sm` breakpoint the same figures read as a
      stacked list instead. The figures are the same variables, so the two cannot disagree.
    */}
    <dl className="sm:hidden grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm m-0" data-testid="trace-stack" aria-label={`How ${name} was divided`}>
      {shares.map(([label, value]) => (
        <div key={label} className="contents">
          <dt className="text-[var(--sgds-body-color-subtle)]">{label}</dt>
          <dd className="m-0 text-right">{value}</dd>
        </div>
      ))}
    </dl>
    <div className="pt-table-wrap hidden sm:block">
      <table className="pt-table">
        <caption className="sr-only">
          How {MSO_COMPONENT_LABELS[component] ?? humaniseToken(component)} was divided
        </caption>
        <thead>
          <tr>
            <th scope="col" data-unsortable="one row of a fixed calculation record, read in the order the engine took it">
              Component
            </th>
            <th scope="col" className="pt-num" data-unsortable="one row of a fixed calculation record, read in the order the engine took it">
              Amount
            </th>
            <th scope="col" className="pt-num" data-unsortable="one row of a fixed calculation record, read in the order the engine took it">
              Officer share
            </th>
            <th scope="col" className="pt-num" data-unsortable="one row of a fixed calculation record, read in the order the engine took it">
              Exact
            </th>
            <th scope="col" className="pt-num" data-unsortable="one row of a fixed calculation record, read in the order the engine took it">
              Rounded
            </th>
            <th scope="col" className="pt-num" data-unsortable="one row of a fixed calculation record, read in the order the engine took it">
              Rounding adjustment
            </th>
            <th scope="col" className="pt-num" data-unsortable="one row of a fixed calculation record, read in the order the engine took it">
              Government
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>{MSO_COMPONENT_LABELS[component] ?? humaniseToken(component)}</td>
            <td className="pt-num">
              <Money cents={amount} />
            </td>
            <td className="pt-num pt-tabular">{basisPointsLabel(basisPoints)}</td>
            <td className="pt-num pt-tabular">{exactCentsAsMoney(exact)}</td>
            <td className="pt-num">
              <Money cents={officer} />
            </td>
            {/* ⚠ Signed text in CENTS ("+0.5000 cents"), so the direction is read, not only seen. */}
            <td className="pt-num pt-tabular">{adjustment === '0.0000' ? 'None' : exactCentsAsMoney(adjustment)}</td>
            <td className="pt-num">
              <Money cents={government} />
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    </div>
  )
}

export interface MsoTraceProps {
  /** The stored trace; `unknown` is accepted because the enquiry reads it from a `jsonb` column. */
  readonly trace: unknown
  readonly heading?: string
  /** The heading level, so the page's outline never skips one: 2 directly under the page title, 3 (default) under a section. */
  readonly level?: 2 | 3
}

export function MsoTrace({ trace, heading = 'How this was calculated', level = 3 }: MsoTraceProps) {
  const steps = readTrace(trace)
  const Heading = level === 2 ? 'h2' : 'h3'
  return (
    <section aria-label={heading} className="pt-card" data-testid="mso-trace">
      <Heading className="text-base font-semibold mb-1">{heading}</Heading>
      <p className="text-sm text-[var(--sgds-body-color-subtle)] mb-3 max-w-[75ch]">
        Every step the engine took, in order. The officer share is rounded to the cent; Government takes the remainder, so
        the two always add up to the bill.
      </p>
      {steps.length === 0 ? (
        <p className="text-sm">No trace was recorded for this calculation.</p>
      ) : (
        <ol className="flex flex-col gap-3 list-none p-0 m-0">
          {steps.map((step) => (
            <li key={step.step} className="pt-well p-3" data-step={step.step}>
              <p className="flex flex-wrap items-baseline gap-2 m-0">
                <span className="pt-mono font-semibold">
                  <span className="sr-only">Step </span>
                  {step.step}
                </span>
                <CategoryBadge kind="trace" value={step.code} label={CODE_LABELS[step.code] ?? humaniseToken(step.code)} />
                <span className="text-sm">{plainSummary(step.summary)}</span>
              </p>
              {step.code === 'COMPONENT_CALCULATED' && <ComponentTable detail={step.detail} />}
            </li>
          ))}
        </ol>
      )}
    </section>
  )
}
